import { buildTesterTestUrl, webRoutes } from '@runhuman/shared';
import { getDiscordSdk } from '../../sdk-client';
import { WEB_BASE_URL } from '../../web-base-url';

/**
 * Open a fully-qualified URL in a real browser window via the Discord SDK's
 * `openExternalLink` command. Discord's iframe can't load the testing flow
 * inside itself (the browser extension can't reach an iframe-sandboxed page),
 * so every web hand-off pops out via this command.
 */
export async function openInBrowser(url: string): Promise<void> {
  await getDiscordSdk().commands.openExternalLink({ url });
}

/**
 * Open the testing harness for a specific job using its testerToken.
 *
 * The URL is built via `buildTesterTestUrl` against the build-time
 * `WEB_BASE_URL` so the shape stays in lockstep with the API's `webTestLink`
 * (server-built post-claim) and the web Tester Portal's relative variant.
 *
 * Used by the In-Progress "Resume testing" button and the late-submission
 * "Resume to submit" button — both have a testerToken on the row.
 *
 * For the post-claim hand-off we call `openInBrowser(webTestLink)` directly
 * with the link from `ClaimJobResponse` — the server already built the
 * absolute URL and there's no client-side reason to rebuild it.
 */
export async function openTestHarness(args: {
  readonly jobId: string;
  readonly testerToken: string;
}): Promise<void> {
  const url = buildTesterTestUrl({ ...args, baseUrl: WEB_BASE_URL });
  await openInBrowser(url);
}

/**
 * Open the web admin job detail page for a job (#3922). Used by the
 * admin-only "View" affordance on stuck-job rows and on in-progress queue
 * rows on the consolidated Activity view. The destination mirrors what an
 * admin would reach from `/admin/jobs` on web — same job detail surface,
 * same auth (Clerk admin) on the other side of the pop.
 *
 * Centralised here so the URL shape stays in one place: every Activity
 * surface that hands off to admin job detail goes through this function.
 */
export async function openAdminJobDetail(jobId: string): Promise<void> {
  const url = new URL(webRoutes.adminJobDetail.build({ jobId }), WEB_BASE_URL).toString();
  await openInBrowser(url);
}

/**
 * Compact "X ago" relative-time formatter shared by every queue row.
 * Discord's iframe is narrow enough that long timestamps push the layout,
 * so the format intentionally collapses to seconds / minutes / hours.
 */
export function formatTimeSinceCreated(createdAt: string | null): string {
  // #4540 — `claimedAt` can be null on unclaimed admin_hold / overdue
  // rows. Surface as "—" rather than rendering "NaNs ago".
  if (createdAt === null) return '—';
  const seconds = Math.floor((Date.now() - new Date(createdAt).getTime()) / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ago`;
}
